import React from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

interface User {
   id: string;
   firstName: string;
   lastName: string;
   email: string;
   status: 'active' | 'inactive';
   accountType?: string;
   location: {
      id?: string;
      latitude: number;
      longitude: number;
      city: string;
      country: string;
      address?: string;
      placeId?: string;
      createdAt?: any;
      updatedAt?: any;
   }; // Note: InteractiveMap only receives users WITH locations
   registrationDate: string;
   lastLogin?: string | null;
   lastActivity?: string | null;
   profile?: {
      photoURL?: string;
      coverPhotoURL?: string;
   } | null;
}

interface InteractiveMapProps {
   users: User[];
   className?: string;
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({ users, className = '' }) => {
   // Status color mapping
   const getStatusColor = (status: string) => {
      switch (status) {
         case 'active':
            return '#10B981'; // Green
         case 'inactive':
            return '#6B7280'; // Gray
         default:
            return '#6B7280'; // Gray
      }
   };

   const getStatusLabel = (status: string) => {
      switch (status) {
         case 'active':
            return 'Active';
         case 'inactive':
            return 'Inactive';
         default:
            return 'Unknown';
      }
   };

   // Default center (world view)
   const defaultCenter: [number, number] = [20, 0];
   const defaultZoom = 2;

   return (
      <div className={`relative ${className}`}>
         <MapContainer
            center={defaultCenter}
            zoom={defaultZoom}
            style={{ height: '100%', width: '100%' }}
            className='rounded-lg'
         >
            <TileLayer
               attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
               url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />

            {users.map((user) => (
               <CircleMarker
                  key={user.id}
                  center={[user.location.latitude, user.location.longitude]}
                  radius={8}
                  fillColor={getStatusColor(user.status)}
                  color='#ffffff'
                  weight={2}
                  opacity={1}
                  fillOpacity={0.8}
               >
                  <Popup>
                     <div className='p-3 min-w-64'>
                        <div className='flex items-center mb-2'>
                           {user.profile?.photoURL && (
                              <img
                                 className='h-8 w-8 rounded-full mr-2'
                                 src={user.profile.photoURL}
                                 alt={`${user.firstName} ${user.lastName}`}
                                 onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                 }}
                              />
                           )}
                           <div>
                              <h3 className='font-semibold text-gray-900'>
                                 {user.firstName} {user.lastName}
                              </h3>
                              {user.accountType && (
                                 <span className='text-xs text-gray-400 capitalize'>
                                    {user.accountType}
                                 </span>
                              )}
                           </div>
                        </div>

                        <p className='text-sm text-gray-600 mb-1'>{user.email}</p>

                        <div className='mb-2'>
                           <p className='text-sm font-medium text-gray-700'>
                              {user.location.city}, {user.location.country}
                           </p>
                           {user.location.address && (
                              <p
                                 className='text-xs text-gray-500 truncate'
                                 title={user.location.address}
                              >
                                 {user.location.address}
                              </p>
                           )}
                           <p className='text-xs text-gray-400'>
                              {user.location.latitude.toFixed(4)},{' '}
                              {user.location.longitude.toFixed(4)}
                           </p>
                        </div>

                        <div className='mb-2'>
                           <span
                              className='inline-flex items-center px-2 py-1 rounded-full text-xs font-medium'
                              style={{
                                 backgroundColor: `${getStatusColor(user.status)}20`,
                                 color: getStatusColor(user.status),
                              }}
                           >
                              {getStatusLabel(user.status)}
                           </span>
                        </div>

                        <div className='border-t pt-2 mt-2'>
                           <p className='text-xs text-gray-500'>
                              Registered: {new Date(user.registrationDate).toLocaleDateString()}
                           </p>
                           {user.lastLogin ? (
                              <p className='text-xs text-gray-500'>
                                 Last login: {new Date(user.lastLogin).toLocaleDateString()}
                              </p>
                           ) : (
                              <p className='text-xs text-gray-400'>Never logged in</p>
                           )}
                           {user.lastActivity ? (
                              <p className='text-xs text-gray-500'>
                                 Last activity: {new Date(user.lastActivity).toLocaleDateString()}
                              </p>
                           ) : (
                              <p className='text-xs text-gray-400'>No activity recorded</p>
                           )}

                           {user.profile && user.profile.coverPhotoURL && (
                              <div className='mt-1'>
                                 <p className='text-xs text-green-600'>✓ Has cover photo</p>
                              </div>
                           )}
                        </div>
                     </div>
                  </Popup>
               </CircleMarker>
            ))}
         </MapContainer>

         {/* Legend */}
         <div className='absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000]'>
            <h4 className='text-sm font-semibold text-gray-900 mb-2'>User Status</h4>
            <div className='space-y-1'>
               <div className='flex items-center space-x-2'>
                  <div
                     className='w-3 h-3 rounded-full border-2 border-white'
                     style={{ backgroundColor: '#10B981' }}
                  ></div>
                  <span className='text-xs text-gray-700'>Active</span>
               </div>
               <div className='flex items-center space-x-2'>
                  <div
                     className='w-3 h-3 rounded-full border-2 border-white'
                     style={{ backgroundColor: '#6B7280' }}
                  ></div>
                  <span className='text-xs text-gray-700'>Inactive</span>
               </div>
            </div>
         </div>
      </div>
   );
};

export default InteractiveMap;
